import type { ComponentPropsWithoutRef } from 'react';
import CurrencyInput from 'react-currency-input-field';
import {
  type FieldValues,
  useController,
  useFormContext,
} from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { cn } from '~/lib/cn';

export type FormFieldCurrencyInputProps<TFormFields extends FieldValues> =
  ComponentProps<
    Omit<FormFieldProps<TFormFields>, 'children'> &
      Omit<
        ComponentPropsWithoutRef<typeof CurrencyInput>,
        'name' | 'className' | 'value' | 'onValueChange'
      >
  >;

export function FormFieldCurrencyInput<TFormFields extends FieldValues>({
  name,
  label,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  fieldMessageClassName,
  triggerClassName,
  showErrorMessage = true,
  ...currencyInputProps
}: FormFieldCurrencyInputProps<TFormFields>) {
  const {
    formState: { isSubmitted },
  } = useFormContext<TFormFields>();

  const {
    field,
    fieldState: { invalid },
  } = useController<TFormFields>({ name });

  const showError = isSubmitted && invalid;

  return (
    <FormField
      name={name}
      fieldMessageClassName={fieldMessageClassName}
      label={label}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      showErrorMessage={showErrorMessage}
    >
      <CurrencyInput
        {...currencyInputProps}
        id={name}
        name={name}
        defaultValue={field.value}
        // https://github.com/cchanxzy/react-currency-input-field?tab=readme-ov-file#onvaluechange
        onValueChange={(_newValue, _name, values) => {
          field.onChange(values?.float ?? 0);
        }}
        onBlur={field.onBlur}
        className={cn(
          'h-[35px] w-full rounded-xs px-sm',
          'text-body-sm bg-emphasis-default text-strong',
          'placeholder:text-subtle',
          'border outline-none border-emphasis-subtle',
          'enabled:hover:border-emphasis-strong enabled:focus:border-secondary-default',
          'disabled:cursor-not-allowed disabled:border-opacity-subtle disabled:text-disabled',
          {
            'border-error-default': showError,
          },
          triggerClassName,
        )}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={showError}
      />
    </FormField>
  );
}

FormFieldCurrencyInput.displayName = 'FormFieldCurrencyInput';
